import { type ChangeEvent, useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { LoadingIcon } from '@/assets/icons/loading-icon'
import { Show } from '@/components/show'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import type { InfinityQueryData } from '@/types/inifity-query/infinity-query-data'
import { TextInput } from '../text-input'
import { useSearchSelect } from './hooks/use-search-select'

export interface SearchSelectProps {
  onChange?: (value: string) => void
  value?: string
  queryFn: (pageParam: number, term: string) => Promise<InfinityQueryData>
  queryKey: string[]
}

export const SearchSelect = ({
  onChange,
  value,
  queryFn,
  queryKey
}: SearchSelectProps) => {
  const [ref, inView] = useInView()

  const {
    states: { data, hasNextPage, isFetching, isError, term },
    actions: { fetchNextPage, setTerm }
  } = useSearchSelect({
    queryFn,
    queryKey
  })

  const handleTerm = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      setTerm(event.target.value)
    },
    [setTerm]
  )

  useEffect(() => {
    if (!inView) return
    if (hasNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [inView])

  return (
    <Select onValueChange={onChange} value={value}>
      <SelectTrigger className="w-full bg-background">
        <SelectValue placeholder="Selecione" />
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-h-52">
        <TextInput
          placeholder="Pesquisar"
          className="mb-2"
          value={term}
          onChange={handleTerm}
        />
        {data?.pages.map((page) =>
          page.data.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.label}
            </SelectItem>
          ))
        )}
        <div ref={ref} className="text-red-600">
          <Show when={isFetching}>
            <div className="w-full flex justify-center mt-2 text-[var(--brand-blue-1)]">
              <LoadingIcon />
            </div>
          </Show>
          <Show when={isError || !data}>
            <p className="text-ring text-center">Nenhum item encontrado</p>
          </Show>
        </div>
      </SelectContent>
    </Select>
  )
}
